import { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { HiOutlineXMark, HiOutlineExclamationCircle, HiOutlineCheckCircle, HiOutlineExclamationTriangle } from 'react-icons/hi2';
import { uploadMappingFile, UploadResult } from '../../services/dataMappingsService';
import { SectionMeta } from './meta';
import './UploadCsvModal.css';

interface UploadCsvModalProps {
  isOpen: boolean;
  section: SectionMeta;
  onClose: () => void;
  onUploaded: () => void;
}

type ModalStep = 'select' | 'preview' | 'done';

function UploadCsvModal({ isOpen, section, onClose, onUploaded }: UploadCsvModalProps) {
  const [step, setStep] = useState<ModalStep>('select');
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<UploadResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setStep('select');
      setFile(null);
      setResult(null);
      setLoading(false);
      setErrorMsg(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const reset = () => {
    setStep('select');
    setFile(null);
    setResult(null);
    setLoading(false);
    setErrorMsg(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (!selected) return;

    setFile(selected);
    setErrorMsg(null);
    setLoading(true);

    try {
      const dryResult = await uploadMappingFile(selected, section.key, true);
      setResult(dryResult);
      setStep('preview');
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : 'Failed to validate file');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!file || !result?.valid) return;

    setLoading(true);
    setErrorMsg(null);

    try {
      const commitResult = await uploadMappingFile(file, section.key, false);
      setResult(commitResult);
      setStep('done');
      toast.success(`Upload complete — ${commitResult.summary.toUpdate} updated, ${commitResult.summary.toInsert} inserted`);
      onUploaded();
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setLoading(false);
    }
  };

  const modeLabel = section.uploadMode === 'upsert'
    ? 'New rows will be inserted; existing rows will be updated.'
    : 'Only rows matching existing records will be updated.';

  return (
    <div className="ucm-overlay">
      <div className="ucm-modal">
        <div className="ucm-header">
          <h2>Upload Data — {section.label}</h2>
          <button type="button" className="ucm-close-btn" onClick={handleClose}>
            <HiOutlineXMark />
          </button>
        </div>

        <div className="ucm-body">
          {step === 'select' && (
            <>
              <p className="ucm-hint">
                Upload a .csv or .xlsx file. Column headers must match the field names for this table.
              </p>
              <p className="ucm-hint ucm-mode-hint">{modeLabel}</p>

              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
              <button
                type="button"
                className="ucm-choose-btn"
                onClick={() => fileInputRef.current?.click()}
                disabled={loading}
              >
                {loading ? 'Validating…' : 'Choose File'}
              </button>
            </>
          )}

          {step === 'preview' && result && (
            <>
              <div className="ucm-summary">
                <div className="ucm-summary-row">
                  <span className="ucm-summary-label">Total rows</span>
                  <span className="ucm-summary-value">{result.summary.totalRows}</span>
                </div>
                {result.summary.toUpdate > 0 && (
                  <div className="ucm-summary-row ucm-summary-success">
                    <HiOutlineCheckCircle />
                    <span>{result.summary.toUpdate} row(s) will be updated</span>
                  </div>
                )}
                {result.summary.toInsert > 0 && (
                  <div className="ucm-summary-row ucm-summary-success">
                    <HiOutlineCheckCircle />
                    <span>{result.summary.toInsert} row(s) will be inserted</span>
                  </div>
                )}
                {result.summary.skipped > 0 && (
                  <div className="ucm-summary-row ucm-summary-warn">
                    <HiOutlineExclamationTriangle />
                    <span>{result.summary.skipped} row(s) skipped (no matching record)</span>
                  </div>
                )}
              </div>

              {result.warnings.map((w, i) => (
                <div className="ucm-warning" key={i}>
                  <HiOutlineExclamationTriangle />
                  <span>{w.message || w.type}</span>
                </div>
              ))}

              {result.errors.length > 0 && (
                <div className="ucm-error">
                  <HiOutlineExclamationCircle />
                  <div>
                    {result.errors.slice(0, 8).map((e, i) => <div key={i}>{e.message}</div>)}
                    {result.errors.length > 8 && <div>…and {result.errors.length - 8} more</div>}
                  </div>
                </div>
              )}
            </>
          )}

          {step === 'done' && result && (
            <div className="ucm-summary">
              <div className="ucm-summary-row ucm-summary-success">
                <HiOutlineCheckCircle />
                <span>Upload complete</span>
              </div>
              <div className="ucm-summary-row">
                <span className="ucm-summary-label">Updated</span>
                <span className="ucm-summary-value">{result.summary.toUpdate}</span>
              </div>
              <div className="ucm-summary-row">
                <span className="ucm-summary-label">Inserted</span>
                <span className="ucm-summary-value">{result.summary.toInsert}</span>
              </div>
              {result.summary.skipped > 0 && (
                <div className="ucm-summary-row">
                  <span className="ucm-summary-label">Skipped</span>
                  <span className="ucm-summary-value">{result.summary.skipped}</span>
                </div>
              )}
            </div>
          )}

          {errorMsg && (
            <div className="ucm-error">
              <HiOutlineExclamationCircle />
              <span>{errorMsg}</span>
            </div>
          )}
        </div>

        <div className="ucm-footer">
          <button type="button" className="ucm-btn-cancel" onClick={handleClose} disabled={loading}>
            {step === 'done' ? 'Close' : 'Cancel'}
          </button>
          {step === 'preview' && (
            <button
              type="button"
              className="ucm-btn-upload"
              onClick={handleConfirm}
              disabled={!result?.valid || loading}
            >
              {loading ? 'Uploading…' : 'Confirm Upload'}
            </button>
          )}
          {step === 'select' && (
            <button type="button" className="ucm-btn-upload" disabled style={{ visibility: 'hidden' }}>
              Upload
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default UploadCsvModal;
